import React, { useState } from 'react';
import { ChevronDown, ChevronUp, TrendingUp, Clock, Sparkles, Search, Globe } from 'lucide-react';
import { SkillNewsItem, NewsUpdate } from '../../services/skillNewsService';

interface SkillNewsCardProps {
  skillNews: SkillNewsItem;
  index: number;
}

const SkillNewsCard: React.FC<SkillNewsCardProps> = ({ skillNews, index }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [currentNewsIndex, setCurrentNewsIndex] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const { skill, latest_updates } = skillNews;
  const displayedNews = isExpanded ? latest_updates : latest_updates.slice(0, 3);

  const formatDate = (date: string) => {
    try {
      // Check if it is a valid date otherwise return 'Not Available'
      if(isNaN(new Date(date).getTime()) || date.length === 0) {
        return 'No Date';
      }
       return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      return 'No Date';
    }

  };
  const handleNext = () => {
    if (currentNewsIndex < latest_updates.length - 1) {
      setIsAnimating(true);
      setCurrentNewsIndex(currentNewsIndex + 1);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handlePrevious = () => {
    if (currentNewsIndex > 0) {
      setIsAnimating(true);
      setCurrentNewsIndex(currentNewsIndex - 1);
      setTimeout(() => setIsAnimating(false), 300);
    }
  };

  const handleDotClick = (newIndex: number) => {
    if (isAnimating || newIndex === currentNewsIndex) return;
    setIsAnimating(true);
    setCurrentNewsIndex(newIndex);
    setTimeout(() => setIsAnimating(false), 300);
  };

  const handleGoogleSearch = (keywords?: string) => {
    if (keywords) {
      const searchUrl = `https://www.google.com/search?q=${encodeURIComponent(keywords)}`;
      window.open(searchUrl, '_blank');
    }
  };

  const getSkillIcon = (skill: string) => {
    const skillLower = skill.toLowerCase();
    if (skillLower.includes('ai') || skillLower.includes('ml') || skillLower.includes('machine learning')) {
      return '🤖';
    } else if (skillLower.includes('react')) {
      return '⚛️';
    } else if (skillLower.includes('python')) {
      return '🐍';
    } else if (skillLower.includes('javascript') || skillLower.includes('js')) {
      return '🟨';
    } else if (skillLower.includes('node')) {
      return '🟢';
    } else if (skillLower.includes('aws') || skillLower.includes('cloud')) {
      return '☁️';
    } else if (skillLower.includes('docker')) {
      return '🐳';
    } else if (skillLower.includes('kubernetes')) {
      return '⚙️';
    } else {
      return '📰';
    }
  };

  return (
    <div 
      className="bg-surface-light/50 dark:bg-surface-dark/50 backdrop-blur-sm border border-border-light dark:border-border-dark hover:border-primary-light/40 dark:hover:border-primary-dark/40 rounded-xl overflow-hidden transition-all duration-300 hover:transform hover:-translate-y-1 hover:shadow-sm"
      style={{ 
        animationDelay: `${index * 0.1}s`,
        animation: 'slideInUp 0.6s ease-out both'
      }}
    >
      {/* Header */}
      <div className="p-4 border-b border-border-light dark:border-border-dark bg-surface-light dark:bg-surface-dark">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* <span className="text-2xl">{getSkillIcon(skill)}</span> */}
            <div>
              <h3 className="text-lg font-bold text-text-primary-light dark:text-text-primary-dark">
                {skill}
              </h3>
              <div className="flex flex-wrap items-center gap-2 md:gap-3 text-sm text-text-secondary-light dark:text-text-secondary-dark mt-1">
                <div className="flex items-center gap-1">
                  <TrendingUp size={14} />
                  <span className='text-text-primary-light dark:text-text-primary-dark'>{latest_updates.length} updates</span>
                </div>
                <div className="flex items-center gap-1 ">
                  <Globe size={14} />
                  <span className='text-text-primary-light dark:text-text-primary-dark'>{latest_updates[currentNewsIndex]['google-search-keywords']?.length && latest_updates[currentNewsIndex]['google-search-keywords'].length > 30 ? latest_updates[currentNewsIndex]['google-search-keywords'].slice(0, 30) + '...' : latest_updates[currentNewsIndex]['google-search-keywords'] || 'No keywords'}</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center px-2 py-1 bg-primary-light/10 dark:bg-primary-dark/10 text-primary-light dark:text-primary-dark rounded-full text-xs font-medium">
              <Sparkles size={12} className="mr-1" />
              Hot
            </span>
          </div>
        </div>
      </div>

      {/* News Content */}
      <div className="p-4">
        {/* Single News View (Inshorts style) */}
        <div className="mb-4">
          <div className="relative overflow-hidden">
            <div 
              className={`bg-background-light/50 dark:bg-background-dark/50 rounded-lg p-4 border border-blue-500/20 dark:border-blue-500/20 transition-all duration-300 ${
                isAnimating ? 'opacity-0 translate-x-2' : 'opacity-100 translate-x-0'
              }`}
            >
              <div className="flex items-start justify-between mb-3">
                <h4 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
                  {latest_updates[currentNewsIndex]?.title}
                </h4>
                {latest_updates[currentNewsIndex]?.['google-search-keywords'] && (
                  <button
                    onClick={() => handleGoogleSearch(latest_updates[currentNewsIndex]?.['google-search-keywords'])}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded-md transition-colors"
                    title="Search on Google"
                  >
                    <Search size={12} />
                    Google
                  </button>
                )}
              </div>
              <p className="text-text-primary-light dark:text-text-primary-dark leading-relaxed text-sm">
                {latest_updates[currentNewsIndex]?.content}
              </p>
            </div>
            {/* Show source and date if available */}
            {(latest_updates[currentNewsIndex]?.source || latest_updates[currentNewsIndex]?.published_date) && (
              <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                {latest_updates[currentNewsIndex]?.source && (
                  <span className="flex items-center gap-1">
                    <span className="">Source:</span>
                    <span className="text-text-primary-light dark:text-text-primary-dark">{latest_updates[currentNewsIndex]?.source}</span>
                  </span>
                )}
                {latest_updates[currentNewsIndex]?.published_date && (
                  <span className="flex items-center gap-1">
                    <span className="">Published:</span>
                    <span className=" text-text-primary-light dark:text-text-primary-dark">{formatDate(latest_updates[currentNewsIndex]?.published_date)}</span>
                  </span>
                )}
              </div>
            )}
          </div>
          
          {/* Navigation for single news view */}
          {latest_updates.length > 1 && (
            <div className="flex items-center justify-between mt-3">
              <button
                onClick={handlePrevious}
                disabled={currentNewsIndex === 0 || isAnimating}
                className="px-3 py-1 text-sm font-medium bg-primary-light/10 dark:bg-primary-dark/10 text-text-primary-light dark:text-text-primary-dark rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-background-light dark:hover:bg-background-dark transition-colors"
              >
                Previous
              </button>
              
              <div className="flex items-center gap-1">
                {latest_updates.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleDotClick(idx)}
                    disabled={isAnimating}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${
                      idx === currentNewsIndex 
                        ? 'bg-primary-light dark:bg-primary-dark scale-125' 
                        : 'bg-border-light dark:bg-border-dark hover:bg-primary-light/50 dark:hover:bg-primary-dark/50'
                    } ${isAnimating ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  />
                ))}
              </div>
              
              <button
                onClick={handleNext}
                disabled={currentNewsIndex === latest_updates.length - 1 || isAnimating}
                className="px-3 py-1 text-sm font-medium bg-primary-light/10 dark:bg-primary-dark/10 text-text-primary-light dark:text-text-primary-dark rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-background-light dark:hover:bg-background-dark transition-colors"
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Expandable List View */}
        {latest_updates.length > 3 && (
          <div>
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 text-primary-light dark:text-primary-dark hover:text-primary-light/80 dark:hover:text-primary-dark/80 transition-colors text-sm font-medium"
            >
              {isExpanded ? (
                <>
                  <ChevronUp size={16} />
                  Show Less
                </>
              ) : (
                <>
                  <ChevronDown size={16} />
                  Other Updates
                </>
              )}
            </button>

            {isExpanded && (
              <div className="mt-3 space-y-2">
                {latest_updates.slice(3).map((update, idx) => (
                  <div
                    key={idx + 3}
                    className="p-4 bg-background-light/30 dark:bg-background-dark/30 rounded-lg border border-primary-light/30 dark:border-primary-dark/30"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h5 className="text-base font-medium text-text-primary-light dark:text-text-primary-dark">
                        {update.title}
                      </h5>
                      {update['google-search-keywords'] && (
                        <button
                          onClick={() => handleGoogleSearch(update['google-search-keywords'])}
                          className="flex items-center gap-1 px-1.5 py-0.5 text-xs bg-blue-500/10 hover:bg-blue-500/20 text-blue-600 dark:text-blue-400 rounded transition-colors"
                          title="Search on Google"
                        >
                          <Search size={12} />
                        </button>
                      )}
                    </div>
                    <p className="text-sm text-text-secondary-light dark:text-text-secondary-dark leading-relaxed">
                      {update.content}
                    </p>
                    {(update.source || update.published_date) && (
                      <div className="flex items-center gap-3 mt-2 text-xs text-text-secondary-light dark:text-text-secondary-dark">
                        {update.source && (
                          <span>Source: <span className="text-text-primary-light dark:text-text-primary-dark">{update.source}</span></span>
                        )}
                        {update.published_date && (
                          <span>Published: <span className="text-text-primary-light dark:text-text-primary-dark">{formatDate(update.published_date)}</span></span>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillNewsCard;